import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PiQrCodeBold } from 'react-icons/pi';

import { FMS_READ_SCOPE } from '#/api/fmsMgmt/fmsReadScope';
import {
  createVoucher,
  deleteDraftVoucher,
  fetchVoucherDetail,
  submitVoucher,
  updateVoucher,
  voucherAttachmentsApi,
} from '#/api/fmsMgmt/voucher';
import { ATTACHMENT_POLICY_STANDARD } from '#/api/platform/attachments';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import {
  DocumentAttachmentsCard,
  uploadPendingAttachmentsAfterCreate,
} from '#/modules/commonComponents/attachments';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import EinvoiceQrCameraDialog from '#/modules/finMgmt/vouchers/components/EinvoiceQrCameraDialog';
import VoucherStatusBadge from '#/modules/finMgmt/vouchers/components/VoucherStatusBadge';
import {
  getVoucherApiErrorMessage,
  isVoucherVersionConflict,
} from '#/modules/finMgmt/vouchers/helper/apiError';
import {
  canDeleteVoucher,
  canEditVoucher,
  canManageVoucherAttachments,
  VOUCHER_TYPE_OPTIONS,
} from '#/modules/finMgmt/vouchers/helper/voucherStatus';
import {
  VOUCHER_FIELD_LABELS,
  VOUCHER_FORM_DEFAULTS,
  voucherFormSchema,
} from '#/modules/finMgmt/vouchers/schemas/voucherFormSchema';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { toast } from '#/utils/toast';
import {
  voucherDetailQueryKey,
  voucherListQueryKeyPrefix,
} from '#/utils/queryKeys';

const BACK_TO = ROUTES.personalCenterVouchersPanel;

const DELETE_CONFIRM = {
  title: '刪除草稿',
  message: '確定刪除此憑證草稿？此操作無法復原。',
  confirmText: '刪除',
  destructive: true,
};

const SUBMIT_CONFIRM = {
  title: '提交憑證',
  message: '確定提交此憑證？提交後將變為有效，主檔不可再修改。',
  confirmText: '提交',
};

const CreateEditVoucherPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updatedAtRef = useRef(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const { confirm, confirmDialog } = useConfirmDialog();
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    // 新增／編輯只在個人中心 → `/self`
    queryKey: voucherDetailQueryKey(id, FMS_READ_SCOPE.SELF),
    queryFn: () => fetchVoucherDetail(id, { scope: FMS_READ_SCOPE.SELF }),
    enabled: Boolean(id),
  });

  const detail = detailQuery.data?.data;
  const status = detail?.status ?? null;

  async function createVoucherWithAttachments(payload) {
    const result = await createVoucher(payload);
    const createdId = result?.data?.id;
    if (createdId) {
      await uploadPendingAttachmentsAfterCreate(
        voucherAttachmentsApi,
        String(createdId),
        pendingFiles,
      );
    }
    return result;
  }

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createVoucherWithAttachments,
    updateFn: updateVoucher,
    buildPayload: (values) => {
      const payload = {
        voucherType: values.voucherType,
        voucherNo: values.voucherNo,
        voucherDate: values.voucherDate,
        totalAmount: Number(values.totalAmount),
        remark: values.remark || null,
      };
      if (id) {
        return { ...payload, updatedAt: updatedAtRef.current };
      }
      return payload;
    },
    listQueryKeyPrefix: voucherListQueryKeyPrefix,
    detailQueryKey: voucherDetailQueryKey,
    getDetailPath: ROUTES.personalCenterVoucherDetail,
    afterUpdateNavigateTo: id ? ROUTES.personalCenterVoucherDetail(id) : null,
    getErrorMessage: (error) => getVoucherApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isVoucherVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(voucherFormSchema),
    defaultValues: VOUCHER_FORM_DEFAULTS,
  });
  const voucherType = useWatch({ control, name: 'voucherType' });
  const [qrScanOpen, setQrScanOpen] = useState(false);

  useEffect(() => {
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset({
      voucherType: detail.voucherType ?? 'INVOICE',
      voucherNo: detail.voucherNo ?? '',
      voucherDate: detail.voucherDate ?? '',
      totalAmount: detail.totalAmount ?? '',
      remark: detail.remark ?? '',
    });
  }, [detail, reset, resetValidationSummary]);

  const deleteMutation = useMutation({
    mutationFn: () => deleteDraftVoucher(id),
    onSuccess: async () => {
      toast.success('草稿已刪除');
      await queryClient.invalidateQueries({
        queryKey: voucherListQueryKeyPrefix(),
      });
      navigate(BACK_TO);
    },
    onError: (error) => {
      toast.error(getVoucherApiErrorMessage(error, '刪除失敗'));
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => submitVoucher(id, { updatedAt: updatedAtRef.current }),
    onSuccess: async (result) => {
      toast.success(result?.message || '提交成功');
      await queryClient.invalidateQueries({
        queryKey: voucherListQueryKeyPrefix(),
      });
      await queryClient.invalidateQueries({
        queryKey: voucherDetailQueryKey(id),
      });
      await detailQuery.refetch();
    },
    onError: (error) => {
      toast.error(getVoucherApiErrorMessage(error, '提交失敗'));
      if (isVoucherVersionConflict(error)) {
        detailQuery.refetch();
      }
    },
  });

  const isEditable = canEditVoucher(status);
  const showDelete = isEdit && canDeleteVoucher(status);
  const showSubmit = isEdit && status === 'DRAFT';
  const isBusy = isPending
    || deleteMutation.isPending
    || submitMutation.isPending;

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  /** 相機掃到電子發票左側 QR：帶入號碼與日期（使用者仍可修改） */
  function handleEinvoiceParsed({ voucherNo, voucherDate }) {
    const options = { shouldDirty: true, shouldValidate: true };
    setValue('voucherNo', voucherNo, options);
    setValue('voucherDate', voucherDate, options);
    setQrScanOpen(false);
    toast.success('已帶入發票號碼與日期');
  }

  function onSubmit(values) {
    if (!isEditable) {
      return;
    }
    markSubmitAttempted();
    save(values);
  }

  async function handleDelete() {
    const ok = await confirm(DELETE_CONFIRM);
    if (!ok) {
      return;
    }
    deleteMutation.mutate();
  }

  async function handleSubmitDraft() {
    const ok = await confirm(SUBMIT_CONFIRM);
    if (!ok) {
      return;
    }
    submitMutation.mutate();
  }

  const pageTitle = isEdit ? '編輯憑證' : '新增憑證';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;

  return (
    <PageShell variant="formTall">
      {confirmDialog}
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-3xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">
                {pageTitle}
              </span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={BACK_TO}>返回</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getVoucherApiErrorMessage(detailQuery.error, '找不到憑證資料')}
            </p>
          ) : (
            <>
              {isEdit && status ? (
                <p
                  className={cn(
                    'mb-3 flex flex-wrap items-center gap-2',
                    'text-sm text-muted-foreground',
                  )}
                >
                  <span>狀態</span>
                  <VoucherStatusBadge status={status} size="sm" />
                  {!isEditable ? <span>（唯讀）</span> : null}
                </p>
              ) : null}

              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={VOUCHER_FIELD_LABELS}
                customMessages={customMessages}
              />
              <form
                className={cn(
                  'sheet create_edit create_edit_form',
                  'create_edit_form--page-scroll',
                )}
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">基本資料</h2>
                    </div>
                    {/* 兩欄：類型｜號碼、日期｜總額；備註跨整列。窄螢幕單欄 */}
                    <div
                      className={cn(
                        'grid grid-cols-1 gap-x-4 gap-y-3',
                        '@2xl/sheet:grid-cols-2',
                      )}
                    >
                      <FloatingSelect
                        id="voucherType"
                        label="憑證類型"
                        options={VOUCHER_TYPE_OPTIONS}
                        control={control}
                        errors={errors}
                        isDisabled={!isEditable}
                      />
                      {/* 發票可開相機掃 QR 帶入號碼／日期；收據號碼格式不固定，只手填 */}
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <FloatingInput
                            id="voucherNo"
                            type="text"
                            label="憑證號碼"
                            register={register}
                            errors={errors}
                            readOnly={!isEditable}
                          />
                        </div>
                        {isEditable && voucherType === 'INVOICE' ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="size-12.5 shrink-0"
                            aria-label="開啟相機掃描發票 QR Code"
                            title="掃描發票 QR Code"
                            onClick={() => setQrScanOpen(true)}
                          >
                            <PiQrCodeBold className="size-6" aria-hidden />
                          </Button>
                        ) : null}
                      </div>
                      <FloatingDateInput
                        id="voucherDate"
                        label="憑證日期"
                        control={control}
                        errors={errors}
                        disabled={!isEditable}
                        readOnly={!isEditable}
                      />
                      <FloatingInput
                        id="totalAmount"
                        type="number"
                        label="總額"
                        register={register}
                        errors={errors}
                        readOnly={!isEditable}
                        min={0}
                        step="0.01"
                      />
                      <div className="@2xl/sheet:col-span-2">
                        <FloatingTextarea
                          id="remark"
                          label="備註"
                          register={register}
                          errors={errors}
                          readOnly={!isEditable}
                        />
                      </div>
                    </div>
                  </section>

                  {isEdit && id ? (
                    <DocumentAttachmentsCard
                      variant="section"
                      className="pb-6"
                      businessId={String(id)}
                      ownerKey="fmsVoucher"
                      api={voucherAttachmentsApi}
                      policy={ATTACHMENT_POLICY_STANDARD}
                      readOnly={!canManageVoucherAttachments(status)}
                    />
                  ) : !isEdit ? (
                    <DocumentAttachmentsCard
                      variant="section"
                      className="pb-6"
                      ownerKey="fmsVoucher"
                      api={voucherAttachmentsApi}
                      policy={ATTACHMENT_POLICY_STANDARD}
                      pendingFiles={pendingFiles}
                      onPendingFilesChange={setPendingFiles}
                    />
                  ) : null}
                </div>

                <div
                  className={cn(
                    'create_edit_form__actions',
                    'flex flex-wrap justify-end gap-2',
                  )}
                >
                  <Button type="button" variant="outline" asChild>
                    <Link to={BACK_TO}>取消</Link>
                  </Button>
                  {showDelete ? (
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={isBusy}
                      onClick={handleDelete}
                    >
                      {deleteMutation.isPending ? '刪除中…' : '刪除草稿'}
                    </Button>
                  ) : null}
                  {showSubmit ? (
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={isBusy}
                      onClick={handleSubmitDraft}
                    >
                      {submitMutation.isPending ? '提交中…' : '提交'}
                    </Button>
                  ) : null}
                  {isEditable ? (
                    <Button type="submit" disabled={isBusy}>
                      {isPending ? '儲存中…' : '儲存'}
                    </Button>
                  ) : null}
                </div>
              </form>
              {qrScanOpen ? (
                <EinvoiceQrCameraDialog
                  open={qrScanOpen}
                  onOpenChange={setQrScanOpen}
                  onParsed={handleEinvoiceParsed}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditVoucherPage;
